'use client'

import { compact, debounce, uniqBy } from 'lodash'
import { useEffect, useState } from 'react'
import { ComboBoxView, type ComboboxProps } from './combo-box'
import { OptionType } from 'types/OptionType'

export type OptionsFetchResponse = {
  options?: OptionType[]
  error?: string
}

export type ComboboxAsyncProps = {
  optionsMapper: (res: any) => OptionType[]
  queryURL?: string
  /**
   * Optional async loader. When supplied, calls it instead of `fetch(queryURL?...)`.
   * Receives search text and any additionalParams. Must return a payload for `optionsMapper`.
   */
  fetcher?: (params: { query: string; [key: string]: any }) => Promise<any>
  additionalParams?: { [key: string]: string }
  additionalChoices?: OptionType[]
  forceOpen?: boolean
  prefetch?: boolean
  onDataLoaded?: (options: OptionType[]) => void
} & ComboboxProps

function defaultFetcher(queryURL: string, params: Record<string, string>): Promise<any> {
  const qs = new URLSearchParams(params).toString()
  return window.fetch(`${queryURL}?${qs}`, { credentials: `include` }).then((r) => r.json())
}

export default function ComboAsync({
  queryURL,
  fetcher,
  additionalParams,
  additionalChoices,
  optionsMapper,
  onChange,
  value,
  forceOpen,
  prefetch,
  onDataLoaded,
  ...rest
}: ComboboxAsyncProps) {
  const loadOptions = (params: Record<string, string>): Promise<any> => {
    if (fetcher) return fetcher(params as { query: string; [key: string]: any })
    if (queryURL) return defaultFetcher(queryURL, params)
    return Promise.resolve({})
  }

  const [data, setData] = useState<OptionType[]>(uniqBy(compact([value].concat(additionalChoices)), (i) => i.id))
  const [firstOpen, setFirstOpen] = useState<boolean>(forceOpen ? true : false)
  const [loading, setLoading] = useState(false)

  const applyResult = (res: any) => {
    const opts = uniqBy(
      compact((additionalChoices || []).concat(optionsMapper(res) as any as OptionType[])),
      (i) => i.id,
    )
    setData(opts)
    onDataLoaded?.(opts)
    setLoading(false)
  }

  const handleSearchValueChange = (text: string) => {
    setLoading(true)
    setFirstOpen(false)
    rest?.onSearchValueChange?.(text)
    loadOptions({ query: text, ...additionalParams })
      .then(applyResult)
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (!firstOpen) return
    setLoading(true)
    loadOptions(additionalParams ?? {})
      .then(applyResult)
      .catch(() => setLoading(false))
  }, [firstOpen])

  useEffect(() => {
    if (!prefetch) return
    loadOptions(additionalParams ?? {})
      .then(applyResult)
      .catch(() => {})
  }, [])

  return (
    <ComboBoxView
      {...rest}
      loading={loading}
      value={value}
      options={data}
      onSearchValueChange={debounce((text) => {
        handleSearchValueChange(text)
      }, 300)}
      onButtonClick={() => {
        setFirstOpen(true)
        rest.onButtonClick?.()
      }}
      onClearSearch={() => handleSearchValueChange(``)}
      onChange={onChange ? onChange : () => {}}
      forceOpen={forceOpen}
    />
  )
}
